import { useState } from "react";
import { Plus, Trash2, Package } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import ParcelPhotoUpload from "./ParcelPhotoUpload";

const MAX_ITEMS = 10;

const blankItem = () => ({
  item_name: "",
  weight_kg: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  quantity: 1,
  customer_package_photo: null,    // Sprint-105a · required base64 data URI
});

/**
 * MultiItemEditor — booking-time editor for adding additional PARCELS to
 * the same pickup/drop. Each parcel is priced separately and the order
 * total is the sum of all parcels.
 *
 * Stays collapsed (just a "+ Add Parcel" prompt) until the customer
 * actually wants more than one parcel — single-parcel bookings keep
 * the legacy form layout untouched.
 *
 * Props:
 *   - items:    list of partial parcels (controlled state)
 *   - onChange: (newItems) => void
 *   - disabled: bool — service types that bundle the order (shifting,
 *               etc.) hide this editor entirely.
 */
export default function MultiItemEditor({ items, onChange, disabled }) {
  const [expanded, setExpanded] = useState((items || []).length > 0);

  if (disabled) return null;

  const startMulti = () => {
    setExpanded(true);
    onChange([blankItem()]);
  };

  const addRow = () => {
    if (items.length >= MAX_ITEMS) return;
    onChange([...items, blankItem()]);
  };

  const removeRow = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
    if (next.length === 0) setExpanded(false);
  };

  const patchRow = (idx, patch) => {
    onChange(items.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  if (!expanded || items.length === 0) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={startMulti}
        className="w-full rounded-xl border-dashed border-orange-300 text-[#FF6B00] hover:bg-orange-50/60 hover:text-[#FF6B00]"
        data-testid="multi-item-toggle"
      >
        <Plus className="h-4 w-4 mr-1.5" />
        Add Parcel (up to {MAX_ITEMS})
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl border border-orange-200 bg-orange-50/40 p-3" data-testid="multi-item-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold uppercase tracking-wider text-[#FF6B00] flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" />
          Parcels in this booking ({items.length}/{MAX_ITEMS})
        </Label>
        <button
          type="button"
          className="text-[11px] text-slate-500 hover:text-slate-700 underline underline-offset-2"
          onClick={() => {
            setExpanded(false);
            onChange([]);
          }}
          data-testid="multi-item-collapse"
        >
          Use single parcel instead
        </button>
      </div>

      {items.map((row, idx) => (
        <div
          key={`item-row-${idx}`}
          className="space-y-2 rounded-xl border border-orange-200/70 bg-white p-3"
          data-testid={`multi-item-row-${idx}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Parcel #{idx + 1}
            </span>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="text-slate-400 hover:text-red-600 transition"
                data-testid={`multi-item-remove-${idx}`}
                aria-label={`Remove parcel ${idx + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-12 gap-2">
            <Input
              className="col-span-7 rounded-lg border-slate-200 h-9 text-sm"
              placeholder="Parcel name / description"
              value={row.item_name}
              onChange={(e) => patchRow(idx, { item_name: e.target.value })}
              data-testid={`multi-item-name-${idx}`}
              required
            />
            <Input
              className="col-span-3 rounded-lg border-slate-200 h-9 text-sm"
              type="number"
              step="0.1"
              min="0.1"
              placeholder="Wt kg"
              value={row.weight_kg}
              onChange={(e) => patchRow(idx, { weight_kg: e.target.value })}
              data-testid={`multi-item-weight-${idx}`}
              required
            />
            <Input
              className="col-span-2 rounded-lg border-slate-200 h-9 text-sm"
              type="number"
              min="1"
              max="50"
              placeholder="Qty"
              value={row.quantity}
              onChange={(e) => patchRow(idx, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
              data-testid={`multi-item-qty-${idx}`}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start gap-2">
            <ParcelPhotoUpload
              value={row.customer_package_photo}
              onChange={(v) => patchRow(idx, { customer_package_photo: v })}
              parcelIndex={idx}
            />
            <div className="grid grid-cols-3 gap-2 flex-1">
              <Input
                className="rounded-lg border-slate-200 h-9 text-sm"
                type="number"
                min="1"
                required
                placeholder="L (cm)"
                value={row.length_cm}
                onChange={(e) => patchRow(idx, { length_cm: e.target.value })}
                data-testid={`multi-item-length-${idx}`}
              />
              <Input
                className="rounded-lg border-slate-200 h-9 text-sm"
                type="number"
                min="1"
                required
                placeholder="W (cm)"
                value={row.width_cm}
                onChange={(e) => patchRow(idx, { width_cm: e.target.value })}
                data-testid={`multi-item-width-${idx}`}
              />
              <Input
                className="rounded-lg border-slate-200 h-9 text-sm"
                type="number"
                min="1"
                required
                placeholder="H (cm)"
                value={row.height_cm}
                onChange={(e) => patchRow(idx, { height_cm: e.target.value })}
                data-testid={`multi-item-height-${idx}`}
              />
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        disabled={items.length >= MAX_ITEMS}
        className="w-full rounded-xl border-dashed border-orange-300 text-[#FF6B00] hover:bg-orange-50/60 hover:text-[#FF6B00]"
        data-testid="multi-item-add"
      >
        <Plus className="h-4 w-4 mr-1.5" />
        {items.length >= MAX_ITEMS ? `Max ${MAX_ITEMS} parcels` : "Add Parcel"}
      </Button>
    </div>
  );
}
